import { useEffect, useRef, useState, FormEvent } from "react";
import { ImagePlus, Loader2, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { invalidateContent, mediaUrl } from "@/lib/content";
import { AREAS, SERVICES } from "@/data/site";

export type Media = { id: string; kind: string; title: string; sector: string; service: string; suburb: string };
export const SECTOR_OPTIONS = ["Warehouse", "Showroom", "Food & Beverage", "Industrial", "Automotive", "Commercial Fitout", "Surface Prep", "Cold Storage"];
export const SUBURBS = [...AREAS.map((a) => a.name), "Other Gold Coast"];

export async function uploadPhoto(file: File, fields: Record<string, string>): Promise<Media> {
  const fd = new FormData();
  fd.append("file", file);
  Object.entries(fields).forEach(([k, v]) => fd.append(k, v));
  const { data } = await api.post("/media", fd);
  return data;
}

const EMPTY = { title: "", sector: "", service: "", suburb: "" };

export default function GalleryManager() {
  const [items, setItems] = useState<Media[]>([]);
  const [f, setF] = useState(EMPTY);
  const [file, setFile] = useState<File | null>(null);
  const [busy, setBusy] = useState(false);
  const input = useRef<HTMLInputElement>(null);
  const set = (k: keyof typeof EMPTY) => (e: { target: { value: string } }) => setF({ ...f, [k]: e.target.value });

  useEffect(() => { api.get("/media", { params: { kind: "gallery" } }).then((r) => setItems(r.data)); }, []);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (!file) return toast.error("Choose a photo first");
    setBusy(true);
    try {
      const m = await uploadPhoto(file, { ...f, kind: "gallery" });
      setItems([m, ...items]);
      setF(EMPTY);
      setFile(null);
      if (input.current) input.current.value = "";
      invalidateContent();
      toast.success("Photo added to website gallery");
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  const remove = async (id: string) => {
    if (!window.confirm("Remove this photo from the website?")) return;
    await api.delete(`/media/${id}`);
    setItems(items.filter((x) => x.id !== id));
    invalidateContent();
  };

  return (
    <section className="border border-slate-800 bg-[#161920] p-5" data-testid="gallery-manager">
      <h2 className="mb-4 font-display text-2xl font-bold uppercase text-white">Gallery photos</h2>
      <form onSubmit={submit} className="grid gap-2 md:grid-cols-6">
        <input ref={input} type="file" accept=".jpg,.jpeg,.png,.webp" onChange={(e) => setFile(e.target.files?.[0] || null)} className="crm-field md:col-span-2" data-testid="gallery-file-input" />
        <input className="crm-field" placeholder="Title" value={f.title} onChange={set("title")} data-testid="gallery-title-input" />
        <select className="crm-field" value={f.sector} onChange={set("sector")} data-testid="gallery-sector-select">
          <option value="">Sector</option>{SECTOR_OPTIONS.map((s) => <option key={s}>{s}</option>)}
        </select>
        <select className="crm-field" value={f.service} onChange={set("service")} data-testid="gallery-service-select">
          <option value="">Service</option>{SERVICES.map((s) => <option key={s.slug}>{s.title}</option>)}
        </select>
        <select className="crm-field" value={f.suburb} onChange={set("suburb")} data-testid="gallery-suburb-select">
          <option value="">Suburb</option>{SUBURBS.map((s) => <option key={s}>{s}</option>)}
        </select>
        <button disabled={busy} className="btn-brand !min-h-0 !py-2 !text-base md:col-span-6" data-testid="gallery-upload-button">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <ImagePlus className="h-4 w-4" />} Upload photo
        </button>
      </form>
      {items.length === 0 ? (
        <p className="mt-5 text-sm text-slate-500" data-testid="gallery-empty">No photos yet — the website shows placeholder images until you upload your own.</p>
      ) : (
        <div className="mt-5 grid grid-cols-2 gap-3 md:grid-cols-4 xl:grid-cols-6">
          {items.map((m) => (
            <figure key={m.id} className="group relative border border-slate-800" data-testid={`gallery-manager-item-${m.id}`}>
              <img src={mediaUrl(m.id)} alt={m.title} className="aspect-[4/3] w-full object-cover" loading="lazy" />
              <figcaption className="p-2 text-xs">
                <div className="truncate text-white">{m.title || "Untitled"}</div>
                <div className="truncate text-slate-500">{[m.sector, m.suburb].filter(Boolean).join(" · ")}</div>
              </figcaption>
              <button onClick={() => remove(m.id)} aria-label="Delete photo" data-testid={`gallery-delete-${m.id}`} className="absolute right-1 top-1 grid h-8 w-8 place-items-center bg-black/70 text-red-300 hover:text-red-400">
                <Trash2 className="h-4 w-4" />
              </button>
            </figure>
          ))}
        </div>
      )}
    </section>
  );
}
